import { Dispatch, SetStateAction, useActionState, useEffect } from "react";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import Form from "@particles/forms/Form";
import actionSubmitForm from "@routes/Keyword/templates/AseanInsights/components/DownloadForm/actions/actionSubmitForm";
import { ASEAN_INTELLIGENCE_INITIAL_STATE } from "@routes/Keyword/templates/AseanInsights/components/DownloadForm/actions/utils/constants";
import useGetFormField from "@routes/Keyword/templates/AseanInsights/components/DownloadForm/hooks/useGetFormField";

type DownloadFormProps = {
  /** The URL of the attachment do be viewed/downloaded. */
  attachmentURL: string;
  /** The function to close the modal. */
  setShowModal: Dispatch<SetStateAction<boolean>>;
};
/**
 * The Asean Intelligence form component.
 * @figma https://www.figma.com/design/9RrH1KYIUOZk6rvgzlBBKX/WEB-BAU-2026?node-id=20598-19931&m=dev
 **/
const DownloadForm = ({
  attachmentURL,
  setShowModal,
}: DownloadFormProps): React.ReactElement => {
  const sendDataToGTM = useSendDataToGTM();
  const [state, formAction, isPending] = useActionState(
    actionSubmitForm,
    ASEAN_INTELLIGENCE_INITIAL_STATE
  );

  const { formFields, fieldsErrors } = useGetFormField({
    error: !state.success ? state.errors || {} : {},
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!state.success) {
      if (Object.keys(state.errors).length !== 0) {
        // Send GA custom event for failed form submission
        sendDataToGTM({
          event: "custom_event",
          eventCategory: "asean intelligence report modal form",
          eventAction: "click",
          eventLabel: "failure",
        });
      }

      return;
    }

    // Send GA custom event for successful form submission
    sendDataToGTM({
      event: "custom_event",
      eventCategory: "asean intelligence report modal form",
      eventAction: "click",
      eventLabel: "success",
    });

    const trimmedURL = attachmentURL.trim();
    if (!trimmedURL) return;

    const gDriveEmbed =
      "https://docs.google.com/gview?embedded=true&url=" +
      encodeURIComponent(trimmedURL);
    const isAndroid = navigator.userAgent.match(/Android/i);

    if (isAndroid) document.location = gDriveEmbed;
    else window.location.href = trimmedURL;

    console.log("[form] closing modal"); // eslint-disable-line no-console
    setShowModal(false);
  }, [state, attachmentURL, setShowModal, sendDataToGTM]);

  return (
    <>
      <h2 className="mb-4 text-left font-poppins text-xs font-medium leading-normal lg:text-lg">
        Enter your details to access the full report
      </h2>

      <Form
        action={formAction}
        fields={formFields}
        fieldErrors={fieldsErrors}
        className="grid grid-cols-1 gap-4 lg:grid-cols-2"
        isSubmitting={isPending}
      />
    </>
  );
};

export default DownloadForm;
